import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import FilterListIcon from "@mui/icons-material/FilterList";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Popover,
  TextField,
} from "@mui/material";

import {
  AggregatedAlertType,
  PasswordMisuseTab,
  RemediationType,
} from "../service/types";

type FilterValues = {
  type: AggregatedAlertType | null;
  remediation: RemediationType | null;
};

type PasswordMisuseFilterToolsProps = {
  onFilterChange: (filters: FilterValues) => void;
  currentTab: PasswordMisuseTab;
};

export const PasswordMisuseFilterTools: React.FC<
  PasswordMisuseFilterToolsProps
> = ({ onFilterChange, currentTab }) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [filters, setFilters] = useState<FilterValues>({
    type: null,
    remediation: null,
  });

  const typeOptions = [
    { value: "phishing", label: t("Phishing") },
    { value: "reuse", label: t("Reuse") },
    { value: "mixed", label: t("Phishing+Reuse") },
  ];

  const remediationOptions = [
    { value: "safe", label: t("Safe") },
    { value: "reuse", label: t("Reuse") },
    { value: "blocked", label: t("Blocked") },
  ];

  useEffect(() => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      type: null,
    }));
    onFilterChange({
      ...filters,
      type: null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApply = () => {
    onFilterChange(filters);
    handleClose();
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<FilterListIcon />}
        onClick={handleClick}
      >
        {t("Filter")}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Card>
          <CardContent sx={{ padding: "10px" }}>
            <Box sx={{ minWidth: "130px", "& > * + *": { marginTop: "10px" } }}>
              {currentTab === "archived" && (
                <Autocomplete
                  options={typeOptions}
                  renderInput={(params) => (
                    <TextField {...params} label={t("Type")} />
                  )}
                  value={
                    typeOptions.find(
                      (option) => option.value === filters.type,
                    ) || null
                  }
                  onChange={(_, newValue) =>
                    setFilters({
                      ...filters,
                      type: newValue
                        ? (newValue.value as AggregatedAlertType)
                        : null,
                    })
                  }
                  getOptionLabel={(option) => option.label}
                />
              )}
              <Autocomplete
                options={remediationOptions}
                renderInput={(params) => (
                  <TextField {...params} label={t("Remediation")} />
                )}
                value={
                  remediationOptions.find(
                    (option) => option.value === filters.remediation,
                  ) || null
                }
                onChange={(_, newValue) =>
                  setFilters({
                    ...filters,
                    remediation: newValue
                      ? (newValue.value as RemediationType)
                      : null,
                  })
                }
                getOptionLabel={(option) => option.label}
              />
            </Box>
          </CardContent>
          <CardActions sx={{ justifyContent: "flex-end" }}>
            <Button onClick={handleApply}>{t("Apply")}</Button>
          </CardActions>
        </Card>
      </Popover>
    </>
  );
};
